/** Default working days: Monday (1) through Friday (5). Values are JS getDay() indices (0=Sun, 6=Sat). */
export const DEFAULT_WORKING_DAYS: readonly number[] = [1, 2, 3, 4, 5];

/**
 * Parse a YYYY-MM-DD string as a local date.
 * new Date("2025-07-01") treats date-only strings as UTC, which can
 * shift the day in timezones behind UTC. This avoids that.
 */
export function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function localMidnightOfUTC(timestamp: number): Date {
  const date = new Date(timestamp * 1000);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * @param d1 - The first date to compare.
 * @param d2 - The second date to compare.
 * @returns True if d1 is before d2
 */
export function isBefore(d1: Date, d2: Date) {
  return d1.getTime() < d2.getTime();
}

export function isAfter(d1: Date, d2: Date) {
  return d1.getTime() > d2.getTime();
}

export function plusDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function plusHours(date: Date, hours: number) {
  const d = new Date(date);
  d.setHours(d.getHours() + hours);
  return d;
}

export function lastWeekDay(d: Date) {
  switch (d.getDay()) {
    case 0: //sunday
      return plusDays(d, -2);
    case 1: //monday
      return plusDays(d, -3);
    default:
      return plusDays(d, -1);
  }
}

export function startOfWeek(d: Date) {
  if (d.getDay() === 0) {
    // sunday
    return plusDays(d, -6);
  } else {
    return plusDays(d, -(d.getDay() - 1));
  }
}

export function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function endOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}

export function weekDaysBetween(d1: Date, d2: Date) {
  let weekdays = 0;
  const d = new Date(d1);
  while (isBefore(d, atDayStart(d2))) {
    if (isWorkingDay(d)) {
      weekdays++;
    }
    d.setDate(d.getDate() + 1);
  }
  if (isWorkingDay(d2)) {
    weekdays += dayFraction(d2);
  }
  return weekdays;
}

function dayFraction(date: Date) {
  return (date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600) / 24;
}

/** Returns true if the given date falls on a working day (Mon–Fri by default). */
export function isWorkingDay(
  date: Date,
  workingDays: readonly number[] = DEFAULT_WORKING_DAYS,
): boolean {
  return workingDays.includes(date.getDay());
}

/**
 * Counts the number of working days between two dates, inclusive.
 */
export function countWorkingDays(
  start: Date,
  end: Date,
  workingDays: readonly number[] = DEFAULT_WORKING_DAYS,
): number {
  let count = 0;
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  const endNormalized = new Date(end);
  endNormalized.setHours(0, 0, 0, 0);

  while (current <= endNormalized) {
    if (isWorkingDay(current, workingDays)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

export function atDayStart(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function minuteDiff(d1: Date, d2: Date) {
  return (d2.getTime() - d1.getTime()) / (1000 * 60);
}

export function dayDiff(d1: Date, d2: Date) {
  return minuteDiff(d1, d2) / (60 * 24);
}

export function isNow(d: Date, tolerance: number = 1000 * 60 * 5) {
  return Math.abs(d.getTime() - new Date().getTime()) < tolerance;
}
